import {
  Patient,
  Practice,
  Professional,
  QuestionnaireVariant,
} from '@aldebaran/prisma';
import {
  Value,
  UserValue,
  UserResponse,
  InsertableQuestion,
  ViewableQuestion,
  ViewableResponse,
  Formula,
} from '../types/global';
import { Question } from './kb';
import { Page } from '../util/sections';
import { ProfessionalWithPractice } from '@aldebaran/common/types';

import { ReportTemplate, Variant } from '@aldebaran/common/types/variant';
import { QuestionnaireVariantFlagMap } from '@aldebaran/common/qVariants';
// The patient app stores variant configs in Redux. A variant's reportTemplate
// can be a resolver function (version-gated), which is non-serializable, so
// findQuestionnaireByVariant collapses it to the 'Variable' marker before
// storage — the patient app only displays it in the debug panel.
export type QuestionnaireConfig = Omit<Variant, 'reportTemplate'> & {
  slug: QuestionnaireVariant;
  reportTemplate: ReportTemplate | 'Variable';
};

type PartialRecord<TKey extends PropertyKey, TValue> = {
  [key in TKey]?: TValue;
};

export type ValueStore = Record<string, UserValue>;

export type UserResponseStore = Record<string, Value>;
export type QuestionResponseStore = Record<string, UserResponseStore>;

type QuestionStore = Record<string, ViewableQuestion>;
type ResponseStore = Record<string, ViewableResponse>;
type SavedRawQuestionStore = Question[];
export type FormulaStore = Record<Formula['name'], Formula['value']>;

export const modes = [
  'SALLE_ATTENTE',
  'INVITATION',
  'PROFESSIONAL_LINK',
  'PRACTICE_LINK',
] as const;
export type Mode = (typeof modes)[number];

export const baseOptions = ['COMPACT', 'ALERTES'] as const;
export const contextOptions = [
  'CONSULTATION_TIME_KNOWN',
  'SALLE_ATTENTE',
  'NO_APPOINTMENT',
  'ASSISTED_BY_PRO',
] as const;

export const displayFullQueueOptions = ['DISPLAY_FULL_QUEUE'] as const;

// Professional-setting-derived feature gates, injected into graph.options so the
// KB can branch on them.
export const featureOptions = ['PATIENT_DOCUMENTS_ENABLED'] as const;

export type Option =
  | (typeof baseOptions)[number]
  | (typeof contextOptions)[number]
  | (typeof displayFullQueueOptions)[number]
  | (typeof featureOptions)[number];

export interface ConsultationState {
  patient?: Patient;
  professional?: ProfessionalWithPractice;
  practice?: Practice & {
    users: Professional[];
    professionalByDefault?: ProfessionalWithPractice | null;
  };
  referrer?: string;
  date?: string;
  noAppointment?: boolean;
  variant?: QuestionnaireVariant;
}

export type TotalQueue = { [key in Page]?: string[] }[];
export interface QuestionnaireState {
  currentQuestion?: ViewableQuestion['extId'];
  currentQuestionIndex: number;
  maxPage: number;
  queue: ViewableQuestion['extId'][];
  validQuestions: ViewableQuestion['extId'][];
  pageAssignments: Record<ViewableQuestion['extId'], string>;
  sectionAssignments: Record<ViewableQuestion['extId'], string>;
  insertedBy: Record<ViewableQuestion['extId'], ViewableQuestion['extId']>;
  insertedByResponses: Record<
    ViewableQuestion['extId'],
    ViewableResponse['extId'][]
  >;
  isQueueReady: boolean;
  totalQueue: TotalQueue;
  consultationTimeKnown?: boolean;
  surveyRatingSent: boolean;
  checklistItems: Record<string, ChecklistItemState[]>;
  sectionQueues: Record<string, ViewableQuestion['extId'][]>;
  interlocuter?: 'SELF' | 'OTHER';
  config?: QuestionnaireConfig;
  areQuestionsUpdating?: boolean;
}

export interface KnowledgeBaseState {
  savedRawQuestions: SavedRawQuestionStore;
  questions: QuestionStore;
  responses: ResponseStore;
  formulas: FormulaStore;
  formulasLoaded: boolean;
}

export interface GraphState {
  assertions: string[];
  assertionsByQuestion: Record<ViewableQuestion['extId'], string[]>;
  assertionsByResponse: Record<ViewableResponse['extId'], string[]>;
  values: ValueStore;
  currentPage: string;
  mode?: Mode;
  variant: QuestionnaireVariant;
  options: PartialRecord<Option, boolean>;
  personalizationFlags: Partial<QuestionnaireVariantFlagMap>;
  /** Computed onboarding/offboarding step sequence */
  onboardingFlow: string[];
  /** Current position in the onboardingFlow array */
  currentStepIndex: number;
  /** Feature flags from PostHog for flow control */
  experimentFlags: Record<string, string>;
}

export interface VersionState {
  current?: number;
  query?: number;
  env?: number;
  api?: number;
}

export interface RootState {
  graph: GraphState;
  questionnaire: QuestionnaireState;
  consultation: ConsultationState;
  userResponses: QuestionResponseStore;
  kb: KnowledgeBaseState;
  sessionId: string;
  questionnaireId?: string;
  // Server revision of the questionnaire this device last saved or loaded,
  // sent with every save (ALDO-2759)
  questionnaireRevision?: number;
  // Set when a save was rejected because another device saved more recently
  questionnaireConflict?: boolean;
  invitationId?: string;
  invitationSlug?: string;
  referrer?: string;
  CGUAccepted?: number;
  researchConsent?: number;
  isFrequentFlyer?: boolean;
  questionsUpdatedCount: number;
  version: VersionState;
  queryVariant?: string;
  previousQueryVariant?: string;
  searchParams: Record<string, string>;
  previousSearchParams: Record<string, string>;
  isFullReset: boolean;
  invitationFallback: boolean;
  patientSegment: string | null;
}

export interface ChecklistItemState {
  value: string;
  family: string;
}

export interface QuestionSummary {
  question: string;
  responses: Value[];
}

export type QuestionnaireSummary = Record<string, QuestionSummary>;

export interface RecordedResponses {
  currentPage: string;
  currentSection?: string;
  responses: UserResponse[];
  questionId: string;
}

export interface QueueModifications {
  toInsert: InsertableQuestion[];
  toDelete: string[];
}
